import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import emergeFromLeft from 'styled-up/dist/keyframes/emergeFromLeft';
import emergeFromRight from 'styled-up/dist/keyframes/emergeFromRight';
import Theme from 'styled-up/dist/Theme';
import Spinner from './Spinner';
import variables from './variables';

const Title = styled('div')`
  font-size: 14px;
  line-height: 18px;
`;

const Description = styled('div')`
  color: #999;
  font-size: 12px;
  line-height: 18px;
`;

const Content = styled('div')``;

const IconWrapper = styled('div')`
  height: 18px;
  margin-left: 20px;
  width: 18px;

  svg {
    display: block;
    fill: currentColor !important;
    max-height: 100%;
    max-width: 100%;
  }
`;

function animation({ position }) {
  return position.indexOf('left') > -1 ? emergeFromLeft : emergeFromRight;
}

const Wrapper = styled('div')`
  align-items: center;
  animation: ${animation} 500ms;
  background-color: #fff;
  border-radius: 5px;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.1), 0 0 20px rgba(0, 0, 0, 0.1);
  color: ${props => variables.color[props.mood]};
  display: flex;
  margin-bottom: 20px;
  padding: 15px 20px;
  width: 300px;
`;

function NotificationIcon({ icon, mood }) {
  if (!icon) {
    return null;
  }

  return (
    <IconWrapper mood={mood}>{icon === 'spinner' ? <Spinner sizePx={18} /> : icon}</IconWrapper>
  );
}

function Notification(props) {
  const { description, icon, mood, onClose, position, timeout, title } = props;

  useEffect(() => {
    if (typeof timeout === 'number' && timeout > 0) {
      setTimeout(() => {
        if (typeof onClose === 'function') {
          onClose();
        }
      }, 1000 * timeout);
    }
  }, []);

  return (
    <Theme isRTL>
      <Wrapper mood={mood} position={position}>
        <NotificationIcon icon={icon} mood={mood} />

        <Content>
          <Title>{title}</Title>

          <Description>{description}</Description>
        </Content>
      </Wrapper>
    </Theme>
  );
}

Notification.propTypes = {
  description: PropTypes.element,
  icon: PropTypes.element,
  mood: PropTypes.oneOf(['error', 'info', 'success', 'warning']).isRequired,
  onClose: PropTypes.func,
  position: PropTypes.string.isRequired,
  timeout: PropTypes.number,
  title: PropTypes.element
};

function getContainer(position) {
  const id = `sweet-notifications-${position}`;

  let container = document.getElementById(id);

  if (!container) {
    const attrs = position.split('-');
    const style = `${attrs[0]}:20px;${attrs[1]}:20px;position:fixed;z-index:9999;`;
    container = document.createElement('div');
    container.setAttribute('id', id);
    container.setAttribute('style', style);
    document.body.appendChild(container);
  }

  return container;
}

function getTarget(position) {
  const container = getContainer(position);
  const target = document.createElement('div');
  container.appendChild(target);
  return target;
}

function render(params = {}) {
  const { description, icon, mood, position, timeout, title } = {
    mood: 'info',
    position: 'top-left',
    timeout: 5,
    ...params
  };

  const target = getTarget(position);

  const destroy = () => target.parentNode.removeChild(target);

  ReactDOM.render(
    <Notification
      description={description}
      icon={icon}
      mood={mood}
      onClose={destroy}
      position={position}
      timeout={timeout}
      title={title}
    />,
    target
  );

  return {
    destroy
  };
}

function sweetNotification(params) {
  return render(params);
}

export default sweetNotification;
